import dynamic from 'next/dynamic';

import { Link } from '@natu/next-link';
import { BreadcrumbsFromUrl } from '@natu/ui/Breadcrumbs/BreadcrumbsFromUrl';
import { Button } from '@natu/ui/Button';
import { Card, CardHeader, CardFooter, CardTitle } from '@natu/ui/Card';
import { DynamicIcon } from '@natu/ui/DynamicIcon';
import { cn } from '@natu/utils/cn';
import { isArrayWithLength } from '@natu/utils/isArrayWithLength';

import { SBProps, getLinkPropsFromStoryblok } from '../../../utils';
import { CareerOfferSchema } from './components/CareerOfferSchema';
import { SBCareerOfferProps } from './types';

const DynamicRender = dynamic(() => import('../../../utils').then(mod => mod.DynamicRender));

const BadgeList = dynamic(() => import('@natu/ui/Badge').then(mod => mod.BadgeList));

const CardContent = dynamic(() => import('@natu/ui/Card').then(mod => mod.CardContent));

const azureBackground = cn(
  'before:bg-naturaily-azureGray relative before:absolute before:z-[-1] before:block before:w-screen',
  'before:-left-4 lg:before:-left-8 xl:before:-left-[calc((100vw-1280px+4rem)/2)]',
  'before:-top-[104px] before:h-[calc(100%+4rem+104px)] lg:before:-top-[122px] lg:before:h-[calc(100%+6rem+122px)]',
);

export const SBCareerOffer = ({ blok }: SBProps<SBCareerOfferProps>) => {
  const { status, title, salaryRange, link, locations, body, afterBody, advancedSeo, parentProps } =
    blok;

  return (
    <>
      {advancedSeo && <DynamicRender data={advancedSeo} parentProps={parentProps} />}
      <CareerOfferSchema blok={blok} />
      <main>
        <BreadcrumbsFromUrl className="mx-auto max-w-screen-xl p-4 lg:px-8 lg:py-6" />
        <div className="mx-auto mt-8 flex max-w-screen-xl items-start justify-between px-4 lg:mt-10 lg:px-8">
          <article className="lg:w-[calc(100%-450px)] xl:w-[calc(100%-500px)]">
            <header className={cn('mb-36 lg:mb-40', azureBackground)}>
              <p className="text-primary-600 text-lg font-semibold">Job offer</p>
              <h1 className="my-4 text-3xl font-semibold md:text-4xl">{title}</h1>
              {salaryRange && <p className="text-base md:text-xl">{salaryRange}</p>}
              {isArrayWithLength(locations) && (
                <BadgeList
                  items={locations.map(location => ({
                    id: location.id,
                    content: location.content.name,
                  }))}
                  className="mt-6"
                />
              )}
            </header>
            <DynamicRender data={body} parentProps={{ className: 'text-lg' }} />
          </article>
          <Card
            className="shadow-2xl-reversed fixed bottom-0 right-0 w-screen lg:sticky lg:top-48 lg:w-[400px] lg:shadow-2xl"
            as="aside"
          >
            <CardHeader className="hidden lg:block">
              <p className="text-primary-600 text-lg font-semibold">
                {status === 'active' ? 'Ongoing' : 'Inactive offer'}
              </p>
              <CardTitle>{title}</CardTitle>
            </CardHeader>
            {(salaryRange || isArrayWithLength(locations)) && (
              <CardContent className="hidden lg:block">
                {salaryRange && <p className="text-lg">{salaryRange}</p>}
                {isArrayWithLength(locations) && (
                  <BadgeList
                    items={locations.map(location => ({
                      id: location.id,
                      content: location.content.name,
                    }))}
                    className="mt-3"
                  />
                )}
              </CardContent>
            )}
            <CardFooter className="mt-0">
              <Button
                asChild
                className={cn(
                  'w-full space-x-2 font-semibold uppercase',
                  status !== 'active' && 'pointer-events-none opacity-50',
                )}
              >
                <Link {...getLinkPropsFromStoryblok(link)}>
                  <span className="text-[13px]">Apply now</span>
                  <DynamicIcon icon="arrowRight" />
                </Link>
              </Button>
            </CardFooter>
          </Card>
        </div>
        <div className="my-36 mb-24 lg:my-24">
          <DynamicRender data={afterBody} parentProps={{ className: 'text-lg' }} />
        </div>
      </main>
    </>
  );
};
